import axios from "axios";
import React, { useState, useRef, useEffect } from "react";
import Stack from "@mui/material/Stack";
import {
  Dialog,
  DialogTitle,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  Typography,
  Button,
  Box,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { useNavigate } from "react-router-dom";
import Panel from "../../../components/v2/panel";
import { BaseURL, helpLine, panelPageMargin } from "../../../utils/consts";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import AudioCapture from "../../../components/v2/audioCapture";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CallIcon from "@mui/icons-material/Call";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import BuddyGreeting from "../../../components/v2/buddyGreeting";
import ChatPrompts from "../../../components/v2/chatPrompts";
import BottomChatBar from "../../../components/v2/bottomChatBar";
import ReactMarkdown from "react-markdown";

const samplePrompts = [
  {
    text: "I've been feeling anxious lately and don't know how to manage it. Can you help?",
    emoji: "🤔",
  },
  {
    text: "I'm struggling with low self-esteem and it's affecting my daily life. What steps can I take?",
    emoji: "💪",
  },
  {
    text: "I find it hard to concentrate on my tasks. Do you have any advice?",
    emoji: "🧠",
  },
  {
    text: "I'm dealing with a lot of work-related stress. How can I cope better?",
    emoji: "🏋️",
  },
  {
    text: "I feel disconnected from my friends and family. How can I improve my relationships?",
    emoji: "💞",
  },
  {
    text: "I'm having trouble finding motivation for my hobbies and interests. What should I do?",
    emoji: "🎨",
  },
  {
    text: "I've been experiencing mood swings that are hard to control. Can we discuss this?",
    emoji: "🌈",
  },
  {
    text: "I find it difficult to manage my anger. Do you have any strategies?",
    emoji: "🤯",
  },
  {
    text: "I'm struggling with grief after losing a loved one. How can I cope with this feeling?",
    emoji: "💔",
  },
  {
    text: "I feel overwhelmed by my financial situation. Can you provide support on managing stress related to this?",
    emoji: "💸",
  },
  {
    text: "I've been having trouble sleeping and it's affecting my daily life. What can I do to improve my sleep?",
    emoji: "🌙",
  },
  {
    text: "I'm not feeling happy with my career path. How can I find what's right for me?",
    emoji: "🔍",
  },
  {
    text: "I feel like I'm not living up to my potential. How can I work on personal growth?",
    emoji: "🌱",
  },
  {
    text: "I'm struggling to maintain a healthy lifestyle. Can you offer guidance?",
    emoji: "🍏",
  },
  {
    text: "I've been experiencing panic attacks. How can I manage these episodes?",
    emoji: "🆘",
  },
  {
    text: "I'm feeling burned out and don't know how to recharge. Do you have any tips?",
    emoji: "🔌",
  },
  {
    text: "I'm dealing with social anxiety and it's impacting my life. How can I cope?",
    emoji: "🗣️",
  },
  {
    text: "I feel stuck in a rut and don't know how to get out. What should I do?",
    emoji: "🔄",
  },
  {
    text: "I'm finding it hard to deal with change and uncertainty. Can we talk about strategies to cope?",
    emoji: "🌀",
  },
  {
    text: "I need help setting and achieving personal goals. Can you guide me?",
    emoji: "🎯",
  },
];
export default function EasyBuddy() {
  const containerRef = useRef(null);
  const [newQuestion, setNewQuestion] = useState("");
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: `Hi ${localStorage.getItem(
        "username"
      )}, I am EasyBuddy, your AI powered personal companion. Feel free to share or ask me anything. Don't worry our conversation would be private.`,
    },
  ]);
  const [loader, setLoader] = useState(false);
  const [language, setLanguage] = useState("english");
  const [isRecording, setIsRecording] = useState(false);
  const [hasAudioAccess, setHasAudioAccess] = useState(false);
  const [reflection, setReflection] = useState({
    isEmergencySupportNeeded: false,
    isConsultationNeeded: false,
    emotionalState: "neutral",
    cognitiveThemes: [],
    behavioralPatterns: [],
    symptoms: "",
    summary: "",
  });
  const changeLanguage = (value) => {
    setLanguage(value);
    setMessages([
      {
        role: "assistant",
        content:
          value === "english"
            ? `Hi ${localStorage.getItem(
                "username"
              )}, I am EasyBuddy, your AI powered personal companion. Feel free to share or ask me anything. Don't worry our conversation would be private.`
            : `नमस्ते ${localStorage.getItem(
                "username"
              )}, मैं EasyBuddy हूँ, आपका AI संचालित व्यक्तिगत साथी। बेझिझक कुछ भी साझा करें या मुझसे पूछें। चिंता न करें, हमारी बातचीत निजी रहेगी।`,
      },
    ]);
  };
  const navigate = useNavigate();
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSubmit = async (question, isReadResponse) => {
    try {
      if (loader) return;
      if (!question && !newQuestion) return;
      let currentMessages = [...messages];
      setMessages([
        ...messages,
        { role: "user", content: question ? question : newQuestion },
      ]);
      currentMessages = currentMessages.concat({
        role: "user",
        content: question ? question : newQuestion,
      });
      setLoader(true);
      setReflection({
        ...reflection,
        isEmergencySupportNeeded: false,
        isConsultationNeeded: false,
      });
      const response = await axios.post(
        // "http://localhost:8000/easy-buddy/generate-relay-response",
        "https://agent.neuronxresearch.com/easy-buddy/generate-relay-response",
        // "https://api.neuronxresearch.com/peacetwogether/chat-agent",
        {
          type: language === "english" ? "easybuddy" : "easybuddy_hindi",
          messages: currentMessages,
          user_response: question ? question : newQuestion,
          symptoms: reflection.symptoms,
          summary: reflection.summary,
        }
      );
      currentMessages = currentMessages.concat({
        role: "assistant",
        content: response.data,
      });
      setMessages(currentMessages);
      setLoader(false);
      setNewQuestion("");
      if (isReadResponse) {
        handleConvertToAudio(response.data);
      }
      const relayResponse = await axios.post(
        // "http://localhost:8000/easy-buddy/generate-relay-reflection",
        "https://agent.neuronxresearch.com/easy-buddy/generate-relay-reflection",
        {
          // type: language === "english" ? "easybuddy" : "easybuddy_hindi",
          messages: currentMessages,
          summary: reflection.summary,
        }
      );
      let symptoms = "Emotional State:" + relayResponse.data.emotionalState;
      if (relayResponse.data.cognitiveThemes.length > 0) {
        symptoms +=
          "\nCognitive Themes: " +
          relayResponse.data.cognitiveThemes.join(", ");
      }
      if (relayResponse.data.behavioralPatterns.length > 0) {
        symptoms +=
          "\nBehavioral Patterns: " +
          relayResponse.data.behavioralPatterns.join(", ");
      }
      setReflection({
        ...relayResponse.data,
        symptoms: symptoms,
      });
    } catch (e) {
      console.log(e.message);
      setLoader(false);
      alert(e.message);
    }
  };
  async function handleConvertToAudio(gptResponse) {
    try {
      const elevenlabsKey = process.env.REACT_APP_ELEVENLABS_KEY;
      let voiceID =
        language === "english"
          ? "sJuaXvoA9JW7mwDM1uLt"
          : "2u9VQe6SvCIfsqLHzhjT";
      const response = await axios.post(
        `https://api.elevenlabs.io/v1/text-to-speech/${voiceID}?optimize_streaming_latency=0&output_format=mp3_44100_128`,
        {
          text: gptResponse,
          model_id:
            language === "english"
              ? "eleven_monolingual_v1"
              : "eleven_multilingual_v2",
          language_id: language === "english" ? "en" : "hi",
          voice_settings: {
            stability: 0,
            similarity_boost: 0,
            style: 0,
            use_speaker_boost: true,
          },
        },
        {
          headers: {
            accept: "audio/mpeg",
            "xi-api-key": elevenlabsKey,
            "Content-Type": "application/json",
          },
          responseType: "arraybuffer", // Ensure the audio data is received as a binary buffer
        }
      );

      playAudio(response.data);
    } catch (error) {
      console.error("Error converting text to audio:", error);
    }
  }
  function playAudio(audioData) {
    const blob = new Blob([audioData], { type: "audio/mpeg" });
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    audio.play();
  }
  const fetchAccess = async () => {
    try {
      const response = await axios.get(
        `${BaseURL}/api/client/settings/audioAccess`,
        {
          headers: {
            "x-auth-token": localStorage.getItem("token"),
          },
        }
      );
      setHasAudioAccess(response.data.hasAudioAccess);
    } catch (e) {
      console.log(e);
    }
  };
  useEffect(() => {
    fetchAccess();
  }, []);

  return (
    <>
      {" "}
      <Stack sx={{ ...panelPageMargin }}>
        <Panel />
        <Stack
          ref={containerRef}
          justifyContent={"start"}
          sx={{
            maxHeight: messages.length > 1 ? "80vh" : "80vh",
            overflowY: "auto",
            overflowX: "hidden",
            paddingBottom: messages.length > 1 ? 15 : 15,
          }}
          pt={2}
          paddingX={{ xs: 1, sm: 4, md: 6 }}
          spacing={{ xs: 0.5, md: 2 }}
        >
          {messages.map(
            (e, index) =>
              e.role !== "system" &&
              index !== 0 && (
                <Paper
                  key={e.content + index}
                  style={{
                    width: "auto",
                    alignSelf: e.role === "user" ? "flex-end" : "flex-start",
                    boxShadow: "none",
                    marginBottom:
                      index === messages.length - 1 &&
                      messages.length > 1 &&
                      !reflection.isEmergencySupportNeeded &&
                      !reflection.isConsultationNeeded
                        ? 300
                        : 5,
                  }}
                >
                  <Box
                    paddingX={4}
                    paddingY={0.2}
                    maxWidth={e.content.length > 140 ? "80%" : "100%"}
                    sx={{
                      fontSize: "20px",
                      backgroundColor:
                        e.role === "user" ? "#ffe3d0" : "#f0f4f9",
                      borderRadius: 3,
                    }}
                  >
                    <ReactMarkdown
                      style={{
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      {e.content}
                    </ReactMarkdown>
                  </Box>
                  {index === messages.length - 1 &&
                    index !== 0 &&
                    e.role === "assistant" && (
                      <Actions
                        navigate={navigate}
                        changeLanguage={changeLanguage}
                        language={language}
                        hasAudioAccess={hasAudioAccess}
                        handleConvertToAudio={handleConvertToAudio}
                        content={e.content}
                      />
                    )}
                </Paper>
              )
          )}
          {reflection.isEmergencySupportNeeded && (
            <Paper>
              {reflection.isEmergencySupportNeeded && (
                <Box
                  sx={{
                    mt: 1,
                    p: 2,
                    bgcolor: "#ffebee",
                    borderRadius: 2,
                    border: "1px solid #f44336",
                  }}
                >
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    color="error.dark"
                  >
                    We're concerned about you and want to make sure you get the
                    support you need right away.
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                    Call our 24/7 Helpline:{" "}
                    <a href={`tel:${helpLine}`}>{helpLine}</a>
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    Please reach out to us immediately. We care about your
                    wellbeing, and our compassionate team is here for you during
                    this difficult time.
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
          {reflection.isConsultationNeeded &&
            !reflection.isEmergencySupportNeeded && (
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  bgcolor: "#f0f4f9",
                  maxWidth: { xs: "100%", md: "70%" },
                  margin: "auto",
                  border: "1px solid #f0f4f9",
                  borderRadius: 2,
                }}
              >
                <Typography
                  sx={{ fontWeight: "medium" }}
                  variant="h5"
                  color="primary.main"
                >
                  We recommend speaking with a professional
                </Typography>
                <Typography variant="h6" align="center">
                  Based on our conversation, it might be helpful to connect with
                  one of our compassionate consultants who can provide
                  personalized support.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ mt: 1 }}
                  onClick={() => navigate(`/v2/client/consultants/${false}`)}
                >
                  View Available Consultants
                </Button>
              </Box>
            )}
          {messages.length === 1 && (
            <BuddyGreeting
              fullText={`I am EasyBuddy, your personal wellness companion.\n Good to see you! Remember, you're not alone. How can I support you today?`}
            />
          )}
          {messages.length === 1 && (
            <Stack
              direction={{ xs: "row" }}
              pl={{ xs: 80, sm: 40, md: 15, lg: 0 }}
              justifyContent={"center"}
              alignItems={"center"}
              spacing={1}
              marginY={2}
              sx={{
                minHeight: { xs: "200px", md: "240px" },
                overflowX: "scroll",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                "-ms-overflow-style": "none" /* IE and Edge */,
                "scrollbar-width": "none" /* Firefox */,
              }}
            >
              <ChatPrompts
                prompts={samplePrompts}
                setNewQuestion={setNewQuestion}
                handleSubmit={handleSubmit}
              />
            </Stack>
          )}
        </Stack>
        <BottomChatBar
          disclaimer="Beta version of AI powered wellness companion. Kindly consult our experts for any serious problem & personal attention."
          newQuestion={newQuestion}
          setNewQuestion={setNewQuestion}
          handleSubmit={handleSubmit}
          setIsRecording={setIsRecording}
          loader={loader}
          hasAudioAccess={hasAudioAccess}
        />
      </Stack>
      <Dialog
        open={isRecording}
        sx={{
          width: "100%",
        }}
        maxWidth="md"
        onClose={() => setIsRecording(false)}
      >
        <DialogTitle>Talk to EasyBuddy</DialogTitle>
        <AudioCapture
          isRecording={isRecording}
          setIsRecording={setIsRecording}
          handleSubmit={handleSubmit}
        />
      </Dialog>
    </>
  );
}
const Actions = ({
  navigate,
  changeLanguage,
  language,
  hasAudioAccess,
  handleConvertToAudio,
  content,
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  return (
    <Stack
      direction={"row"}
      gap={1}
      spacing={1}
      alignSelf={{ xs: "start" }}
      paddingX={1}
      paddingY={1}
      color={"gray"}
    >
      <Tooltip title="Consult An Expert">
        <IconButton
          size="large"
          sx={{ p: 0.5 }}
          onClick={() => navigate(`/v2/client/consultants/${false}`)}
        >
          <CalendarMonthIcon
            sx={{ cursor: "pointer", "&:hover": { color: "purple" } }}
          />
        </IconButton>
      </Tooltip>
      <Tooltip title="Instant Support">
        <IconButton
          size="large"
          sx={{ p: 0.5 }}
          onClick={() => window.open(`tel:${helpLine}`)}
        >
          <CallIcon
            sx={{ cursor: "pointer", "&:hover": { color: "purple" } }}
          />
        </IconButton>
      </Tooltip>
      <Tooltip title="Listen To Audio">
        {hasAudioAccess && (
          <IconButton
            size="large"
            sx={{ p: 0.5 }}
            onClick={() => handleConvertToAudio(content)}
          >
            <VolumeUpIcon
              sx={{
                cursor: "pointer",
                color: "gray",
                "&:hover": { color: "purple" },
              }}
            />
          </IconButton>
        )}
      </Tooltip>
      <Tooltip title="More">
        <IconButton
          size="large"
          sx={{ p: 0.5 }}
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          <MoreVertIcon
            sx={{ cursor: "pointer", "&:hover": { color: "purple" } }}
          />
        </IconButton>
      </Tooltip>
      <Menu
        id="lock-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{
          "aria-labelledby": "lock-button",
          role: "listbox",
        }}
      >
        {[
          { label: "Chat In English", language: "english" },
          { label: "Chat In Hindi", language: "hindi" },
        ].map((option) => (
          <MenuItem
            key={option.label}
            selected={language === option.language}
            onClick={() => {
              setAnchorEl(null);
              changeLanguage(option.language);
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
};
